import styled from "styled-components";
import fontSize from "../../../theme/fonts/FontTheme";
import TextField from "../../../components/input/TextField";
import { useReducer } from "react";
import PhoneField from "../../../components/input/PhoneField";
import TextArea from "../../../components/input/TextArea";
import Dropdown from "../../../components/input/Dropdown";
import H1 from "../../../components/typography/H1";
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import colors from "../../../theme/color/ColorTheme";
import {
  validateEmail,
  validateRequired,
  validatePhone,
  validateDropdown,
} from "../../../utils/validators";

const Wrapper = styled.section`
  padding: 0 40px;
  display: flex;
  gap: clamp(2.5rem, (48 / 1920) * 100vw, 3rem);
  margin-bottom: clamp(6rem, (100 / 1920) * 100vw, 6.25rem);

  @media (max-width: 740px) {
    flex-direction: column;
    padding: 0 20px;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(1.5rem, (32 / 1920) * 100vw, 2rem);
  max-width: clamp(19.5rem, (320 / 1920) * 100vw, 20rem);

  @media (max-width: 740px) {
    max-width: 100%;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.25rem, (8 / 1920) * 100vw, 0.5rem);
`;

const Paragraph = styled.p`
  letter-spacing: 0.08rem;
  font-size: ${fontSize.xs};
  line-height: 1.1rem;
`;

const Form = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  row-gap: clamp(1.6rem, (40 / 1920) * 100vw, 2.5rem);
  column-gap: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: clamp(1.6rem, (40 / 1920) * 100vw, 2.5rem);
  column-gap: 16px;
`;

const Acknowledgement = styled.p`
  font-size: ${fontSize.xxs};
  color: ${colors.primaryGray};
`;

const initialState = {
  countryCode: "+39",
  values: {
    firstName: "",
    lastName: "",
    email: "",
    topic: "",
    phone: "",
    message: "",
  },
  errors: {
    firstName: "",
    lastName: "",
    email: "",
    topic: "",
    phone: "",
    message: "",
  },
};

function formReducer(state, action) {
  switch (action.type) {
    case "SET_FIELD":
      return {
        ...state,
        values: { ...state.values, [action.field]: action.value },
      };
    case "SET_ERRORS":
      return {
        ...state,
        errors: action.errors,
      };
    case "CLEAR_ERRORS":
      return {
        ...state,
        errors: initialState.errors,
      };
    case "SET_COUNTRY_CODE":
      return {
        ...state,
        countryCode: action.value,
      };

    default:
      return state;
  }
}

function ContactForm() {
  const [state, dispatch] = useReducer(formReducer, initialState);

  const handleChange = (field) => (e) => {
    dispatch({ type: "SET_FIELD", field, value: e.target.value });
  };

  const validate = (values) => {
    const errors = {};

    errors.firstName = validateRequired(values.firstName);
    errors.lastName = validateRequired(values.lastName);
    errors.email = validateEmail(values.email);
    errors.topic = validateDropdown(values.topic);
    errors.phone = validatePhone(values.phone);
    errors.message = validateRequired(values.message);

    Object.keys(errors).forEach((key) => !errors[key] && delete errors[key]);

    return errors;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate(state.values);
    dispatch({ type: "SET_ERRORS", errors });

    if (Object.keys(errors).length === 0) {
      console.log("Submitting form:", state.values);
      dispatch({ type: "CLEAR_ERRORS" });
      // call API here
    }
  };

  return (
    <Wrapper>
      <Info>
        <Column>
          <H1>CONTACT FORM</H1>
          <Paragraph>
            Please fill in this form to send us a message. Our Client Service
            advisors will respond to your query as soon as possible.
          </Paragraph>
        </Column>
      </Info>
      <Form>
        <FormWrapper>
          <Row>
            <TextField
              text="FIRST NAME"
              value={state.values.firstName}
              error={state.errors.firstName}
              onChange={handleChange("firstName")}
            />
            <TextField
              text="LAST NAME"
              value={state.values.lastName}
              error={state.errors.lastName}
              onChange={handleChange("lastName")}
            />
          </Row>
          <Row>
            <TextField
              text="EMAIL"
              type="email"
              value={state.values.email}
              error={state.errors.email}
              onChange={handleChange("email")}
            />
            <Dropdown
              value={state.values.topic}
              onChange={(val) =>
                dispatch({ type: "SET_FIELD", field: "topic", value: val })
              }
              options={[
                "Product Information",
                "Company Information",
                "Return & Refunds",
                "Shipping",
                "Payment",
              ]}
              placeholder="SELECT TOPIC"
              error={state.errors.topic}
            />
          </Row>
          <PhoneField
            value={state.values.phone}
            countryCode={state.countryCode}
            error={state.errors.phone}
            onChangeValue={(val) =>
              dispatch({ type: "SET_FIELD", field: "phone", value: val })
            }
            onChangeCode={(code) =>
              dispatch({ type: "SET_COUNTRY_CODE", value: code })
            }
          />
          <TextArea
            text={"MESSAGE"}
            value={state.values.message}
            error={state.errors.message}
            onChange={handleChange("message")}
          />
        </FormWrapper>

        <Acknowledgement>
          I acknowledge that my data will be processed by Peak Pashmina in
          accordance with the provisions of the Privacy Policy.
        </Acknowledgement>

        <PrimaryButton
          onClick={handleSubmit}
          title="Continue "
          width="min(100%, 50%)"
        />
      </Form>
    </Wrapper>
  );
}

export default ContactForm;
